import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Percent, Coins, Package, Send, ScanLine } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProductPicker from "@/components/ProductPicker";
import { SingleView } from "@/components/ComparisonView";
import { modeForProduct, chf, fmt, num, DEFAULT_PARAMS } from "@/lib/calc";

const API = process.env.REACT_APP_BACKEND_URL;

// "Scegli Prodotto": same rich single-product view as the comparison, plus optional
// artisan markup (default 0) and PDF export. No charts.
export default function QuoteBuilder({ categories, refreshKey, onOpenUpload }) {
  const { t, quoteDraft, setQuoteDraft } = useApp();
  const [product, setProduct] = useState(null);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [ov, setOv] = useState({});
  const [packInit, setPackInit] = useState(null);
  const [markup, setMarkup] = useState(0);
  const [calc, setCalc] = useState(null); // { material, area, price, pack, unit }
  const [presetArea, setPresetArea] = useState(null); // m² carried over from the measure page
  const [presetRooms, setPresetRooms] = useState([]); // per-room breakdown carried from the measure page
  const [form, setForm] = useState({ company: "", project: "", city_zip: "", recipient_email: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const spec = useMemo(() => ({ ...(product?.specs || {}), ...ov }), [product, ov]);
  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const selectProduct = (pr) => {
    setProduct(pr);
    setOv({});
    // keep the measured/entered area when switching product
    setParams((p) => ({ ...DEFAULT_PARAMS, area_m2: p.area_m2 }));
    setPackInit(null);
    setMarkup(0);
    setCalc(null);
  };

  // Product carried over from the comparison keeps its edited price + params
  useEffect(() => {
    if (!quoteDraft) return;
    const pr = quoteDraft.product;
    setProduct(pr);
    setParams(quoteDraft.params || DEFAULT_PARAMS);
    setPresetArea(quoteDraft.fromMeasure ? quoteDraft.area : null);
    setPresetRooms(quoteDraft.fromMeasure && Array.isArray(quoteDraft.rooms) ? quoteDraft.rooms : []);
    const isPack = modeForProduct(quoteDraft.spec || pr?.specs || {}, pr?.subcategory) === "pack";
    if (isPack) {
      setOv({});
      setPackInit({ qty: quoteDraft.area, price: quoteDraft.price });
    } else {
      setOv(quoteDraft.price != null ? { price_per_kg: quoteDraft.price } : {});
      setPackInit(null);
    }
    setMarkup(0);
    setCalc(null);
    setQuoteDraft(null);
  }, [quoteDraft, setQuoteDraft]);

  const material = calc?.material ?? null;
  const clientPrice = material != null ? material * (1 + num(markup) / 100) : null;
  const requiredOk = form.company.trim() && form.project.trim() && form.city_zip.trim();
  const pack = calc?.pack;
  const unitLabel = pack ? (calc?.unit === "m2" ? "m²" : calc?.unit === "m" ? "m" : "pz") : "m²";
  const qtyLabel = calc ? (pack ? `${fmt(num(calc.packs), 1)} conf.` : `${fmt(num(calc.bags), 0)} ${t("bagsNeeded").toLowerCase()}`) : null;
  const acquaPct = num(spec.acqua);
  const waterLiters = !pack && calc && acquaPct > 0 && acquaPct <= 60 ? Math.round(num(calc.totalKg) * acquaPct / 100) : null;

  const buildPDF = () => {
    const doc = new jsPDF();
    const W = doc.internal.pageSize.getWidth();
    doc.setFillColor(255, 192, 0); doc.rect(0, 0, W, 22, "F");
    doc.setTextColor(17, 17, 17); doc.setFontSize(18); doc.setFont(undefined, "bold");
    doc.text("METEROX", 14, 14);
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    doc.text(t("quoteTitle"), W - 14, 14, { align: "right" });
    doc.setTextColor(60, 60, 60); doc.setFontSize(9);
    doc.text(`${form.company} · ${form.project} · ${form.city_zip}`, 14, 30);
    doc.text(new Date().toLocaleDateString(), W - 14, 30, { align: "right" });

    const body = [
      [t("product"), `${product.brand} ${product.name}`],
      [pack ? t("packQty") : t("metratura"), `${fmt(num(calc?.area), 1)} ${unitLabel}`],
      [t("bagsNeeded"), qtyLabel || "—"],
    ];
    if (waterLiters != null) body.push([t("acqua"), `${waterLiters} l`]);
    body.push([pack ? t("packPrice") : t("pricePerKg"), pack ? chf(num(calc?.price)) : `${chf(num(calc?.price))}/kg`]);
    body.push([t("productCost"), chf(material)]);
    body.push([`${t("markup")} (%)`, `${fmt(num(markup), 0)}%`]);
    if (form.notes.trim()) body.push([t("fNotes"), form.notes.trim()]);

    autoTable(doc, {
      startY: 38, head: [[t("quoteDetail"), ""]], body,
      theme: "grid", headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 }, bodyStyles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right" } },
    });
    if (presetRooms.length > 1) {
      autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 4,
        head: [[t("roomsSummary"), "m²"]],
        body: presetRooms.map((r) => [r.name, fmt(num(r.area), 2)]),
        foot: [[t("metratura"), fmt(num(presetArea), 2)]],
        theme: "grid", headStyles: { fillColor: [17, 17, 17], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 }, footStyles: { fillColor: [240, 240, 240], textColor: 17, fontStyle: "bold", fontSize: 9 },
        columnStyles: { 1: { halign: "right" } },
      });
    }
    const y = doc.lastAutoTable.finalY + 4;
    doc.setFillColor(255, 192, 0); doc.rect(14, y, W - 28, 12, "F");
    doc.setTextColor(17, 17, 17); doc.setFont(undefined, "bold"); doc.setFontSize(12);
    doc.text(t("clientPrice"), 18, y + 8);
    doc.text(chf(clientPrice), W - 18, y + 8, { align: "right" });
    doc.setFont(undefined, "normal"); doc.setFontSize(7.5); doc.setTextColor(120, 120, 120);
    doc.text(doc.splitTextToSize(t("disclaimerShort"), W - 28), 14, y + 22);
    return doc;
  };

  const onSend = async () => {
    if (!product || material == null || !requiredOk) return;
    setSaving(true);
    const doc = buildPDF();
    const safe = (form.project || product.name).replace(/\s+/g, "_");
    doc.save(`METEROX_${safe}.pdf`);
    const pdf_base64 = doc.output("datauristring");
    try {
      const r = await fetch(`${API}/api/send-quote`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          company: form.company, project: form.project, city_zip: form.city_zip,
          recipient_email: form.recipient_email || null, notes: form.notes,
          product_name: product.name, brand: product.brand,
          area_m2: num(calc?.area), unit: unitLabel, qty_label: qtyLabel,
          water_liters: waterLiters, material_cost: material, markup_pct: num(markup),
          client_price: clientPrice, rooms: presetRooms, pdf_base64,
        }),
      });
      const data = await r.json();
      const recip = data?.results?.recipient;
      if (form.recipient_email && recip && !recip.ok) {
        toast.warning(`${t("toastDownloaded")} · email destinatario non inviata`);
      } else {
        toast.success(form.recipient_email ? t("toastSent") : t("toastDownloaded"));
      }
    } catch {
      toast.success(t("toastDownloaded"));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="quote-builder">
      <div className="max-w-2xl">
        <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl">{t("quoteTitle")}.</h1>
        <p className="mt-3 text-base text-muted-foreground">{t("quoteSubtitle")}</p>
      </div>

      {presetArea != null && (
        <div data-testid="preset-area-banner" className="border border-primary bg-primary/10 px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="flex items-center gap-2 font-bold text-foreground">
              <ScanLine className="h-5 w-5 text-primary" />
              {t("measureReady")}: <span className="font-mono">{fmt(presetArea, 2)} m²</span>
            </span>
            <span className="text-sm text-muted-foreground">{t("measurePickHint")}</span>
          </div>
          {presetRooms.length > 1 && (
            <div className="mt-3 border-t border-primary/30 pt-2" data-testid="preset-rooms">
              <span className="label-caps text-muted-foreground">{t("roomsSummary")}</span>
              <ul className="mt-1.5 grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
                {presetRooms.map((r, i) => (
                  <li key={i} className="flex items-center justify-between border-b border-dashed border-primary/20 py-0.5 text-sm">
                    <span className="truncate text-foreground">{r.name}</span>
                    <span className="ml-3 shrink-0 font-mono text-muted-foreground">{fmt(num(r.area), 2)} m²</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="max-w-xl">
        <ProductPicker
          side="q" categories={categories} value={product} refreshKey={refreshKey}
          onSelect={selectProduct}
          onOpenUpload={(sc) => onOpenUpload && onOpenUpload(sc)}
          enableFilters
        />
      </div>

      {product ? (
        <>
          <SingleView
            product={product} spec={spec} ov={ov} setOv={setOv}
            params={params} setParam={setParam} t={t}
            onResult={setCalc} packInit={packInit} simple
          />

          <div className="border border-border border-l-4 border-l-primary bg-card p-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <span className="label-caps flex items-center gap-1.5 text-muted-foreground"><Percent className="h-3.5 w-3.5 text-primary" /> {t("markup")}</span>
                <Input data-testid="quote-markup" type="number" inputMode="decimal" value={markup} onChange={(e) => setMarkup(e.target.value)} className="mt-1.5 rounded-none border-border font-mono" />
              </div>
              <div className="flex flex-col justify-end">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5 text-muted-foreground"><Package className="h-4 w-4" /> {t("productCost")}</span>
                  <span data-testid="quote-material" className="font-mono font-bold">{material != null ? chf(material) : "—"}</span>
                </div>
                <div className="mt-1.5 flex items-center justify-between bg-primary/10 px-3 py-2">
                  <span className="flex items-center gap-1.5 font-bold text-foreground"><Coins className="h-4 w-4 text-primary" /> {t("clientPrice")}</span>
                  <span data-testid="quote-client-price" className="font-mono text-xl font-black text-foreground">{clientPrice != null ? chf(clientPrice) : "—"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Condividi risultati / Invia ordine */}
          <div className="border border-border bg-card p-4" data-testid="share-form">
            <h3 className="mb-1 font-display text-lg font-bold">{t("shareTitle")}</h3>
            <p className="mb-3 text-xs text-muted-foreground">{t("shareHint")}</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input data-testid="share-company" placeholder={t("fCompany")} value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} className="rounded-none border-border" />
              <Input data-testid="share-project" placeholder={t("fProject")} value={form.project} onChange={(e) => setForm((f) => ({ ...f, project: e.target.value }))} className="rounded-none border-border" />
              <Input data-testid="share-cityzip" placeholder={t("fCityZip")} value={form.city_zip} onChange={(e) => setForm((f) => ({ ...f, city_zip: e.target.value }))} className="rounded-none border-border" />
              <Input data-testid="share-recipient" type="email" placeholder={t("fRecipient")} value={form.recipient_email} onChange={(e) => setForm((f) => ({ ...f, recipient_email: e.target.value }))} className="rounded-none border-border" />
            </div>
            <textarea data-testid="share-notes" placeholder={t("fNotes")} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2} className="mt-3 w-full resize-none rounded-none border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary" />
            <Button data-testid="quote-send" onClick={onSend} disabled={material == null || !requiredOk || saving} className="mt-3 w-full rounded-none bg-primary text-primary-foreground hover:opacity-90">
              <Send className="mr-2 h-4 w-4" /> {saving ? "…" : t("btnSend")}
            </Button>
            {!requiredOk && <p className="mt-2 text-[11px] text-muted-foreground">{t("shareRequired")}</p>}
            <p className="mt-3 border-t border-border pt-2 text-[10px] leading-snug text-muted-foreground/70">{t("disclaimerShort")}</p>
          </div>
        </>
      ) : (
        <p data-testid="quote-empty" className="text-sm text-muted-foreground">{t("quoteEmptyBody")}</p>
      )}
    </div>
  );
}
